import QuickLRU from 'quick-lru';

import type {
  SkillRef,
  SkillTaxonomySecondarySelectionListQuery,
} from '@revelio/data-access';

import { removeBadSkills } from '../../../../shared/selection-lists/utils/removeBadSkills';

export type CustomSkillTreeNode = {
  id: string;
  name: string;
  depth: number;
  suggestedTitles?: string[];
  taxonomyTitles?: string | readonly (string | null)[] | null;
  children?: CustomSkillTreeNode[];
};

type SkillSelectionList =
  SkillTaxonomySecondarySelectionListQuery['selectionList'];

export function buildSkillTree(
  selectionList: SkillSelectionList
): CustomSkillTreeNode[] | null {
  if (!selectionList) return null;

  const levels = [
    removeBadSkills(selectionList.skill_0?.refs) || [],
    removeBadSkills(selectionList.skill_1?.refs) || [],
    removeBadSkills(selectionList.skill_2?.refs) || [],
    removeBadSkills(selectionList.skill_3?.refs) || [],
    removeBadSkills(selectionList.skill_4?.refs) || [],
  ];

  if (!levels[0] || levels[0].length === 0) return null;

  const parentMaps = buildParentMaps(levels);
  return buildLevel(levels[0], 0, levels, parentMaps, '');
}

export function buildLevelIdMap(
  selectionList: SkillSelectionList
): Record<string, number> {
  const map: Record<string, number> = {};
  if (!selectionList) return map;

  (['skill_0', 'skill_1', 'skill_2', 'skill_3', 'skill_4'] as const).forEach(
    (level) => {
      const levelId = selectionList[level]?.label?.level_id;
      if (typeof levelId === 'number') {
        map[level] = levelId;
      }
    }
  );

  return map;
}

export function getSkillTreeCacheKey(
  selectionList: SkillSelectionList
): string | null {
  if (!selectionList) return null;

  const parts = [
    selectionList.skill_0?.tax_id,
    selectionList.skill_0?.tax_version,
    selectionList.skill_1?.tax_id,
    selectionList.skill_1?.tax_version,
    selectionList.skill_2?.tax_id,
    selectionList.skill_2?.tax_version,
    selectionList.skill_3?.tax_id,
    selectionList.skill_3?.tax_version,
    selectionList.skill_4?.tax_id,
    selectionList.skill_4?.tax_version,
  ];

  const hasAny = parts.some((p) => p !== undefined && p !== null);
  if (!hasAny) return null;

  return parts.map((p) => String(p ?? '')).join('|');
}

function buildParentMaps(levels: SkillRef[][]): Map<string, SkillRef[]>[] {
  return levels.slice(1).map((children, idx) => {
    const map = new Map<string, SkillRef[]>();
    children?.forEach((child) => {
      const parentId = child.parents?.[idx];
      if (parentId && parentId !== child.id) {
        const pid = String(parentId);
        map.set(pid, [...(map.get(pid) || []), child]);
      }
    });
    return map;
  });
}

function buildLevel(
  refs: SkillRef[],
  depth: number,
  levels: SkillRef[][],
  parentMaps: Map<string, SkillRef[]>[],
  prefix: string
): CustomSkillTreeNode[] {
  return refs.map((ref) => {
    const nodeId = String(ref.id);
    const id = prefix ? `${prefix}_${nodeId}` : nodeId;

    const node: CustomSkillTreeNode = {
      id,
      name: ref.short_name || ref.long_name || '',
      depth,
    };

    if (ref.titles?.length) {
      node.suggestedTitles = ref.titles;
    }

    const childRefs = parentMaps[depth]?.get(nodeId);
    if (childRefs?.length && depth < levels.length - 1) {
      node.children = buildLevel(childRefs, depth + 1, levels, parentMaps, id);
    }

    return node;
  });
}

class SkillTreeCache {
  private cache = new QuickLRU<string, CustomSkillTreeNode[]>({ maxSize: 2 });

  get(
    cacheKey: string,
    selectionList: SkillSelectionList
  ): CustomSkillTreeNode[] | null {
    if (!cacheKey || !selectionList) return null;

    const cached = this.cache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const result = buildSkillTree(selectionList);
    if (result) {
      this.cache.set(cacheKey, result);
    }

    return result;
  }

  clear(): void {
    this.cache.clear();
  }
}

export const skillTreeCache = new SkillTreeCache();
